/**
 * 純ロジック
 *
 * 値の変換と判定だけを置く。editor が必要なものは最小の構造だけを受け取り @tiptap の型に依存しない
 */
import { INDENT_MAX, INDENT_STEP_PX } from './config';

/** paragraphValueOf が参照する editor の最小構造 */
export interface BlockStateLike {
  isActive(name: string, attrs?: Record<string, unknown>): boolean;
}

/** keydown の判定に使う最小構造。keyCode は IME 検出のためのレガシー値 */
export interface EnterKeyLike {
  key: string;
  keyCode?: number;
}

/** ツールバー構成の token 文字列を分解する。空要素は捨てる */
export const parseToolbarSpec = (spec: string): string[] =>
  spec
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token !== '');

/**
 * 「直後に item が続く item」へ結合用クラスを付ける
 * CSS の `:has(+ .codmon-rte__item)` が対象ブラウザ (chrome>=81 / safari>=13.1) で未サポートなため
 */
export const markJoinedItems = (container: Element): void => {
  for (const item of Array.from(container.querySelectorAll('.codmon-rte__item'))) {
    const next = item.nextElementSibling;
    item.classList.toggle('codmon-rte__item--joined-right', !!next && next.classList.contains('codmon-rte__item'));
  }
};

/** 現在の段落スタイル値 (STYLE_LIST の value と対応) */
export const paragraphValueOf = (editor: BlockStateLike): string => {
  if (editor.isActive('blockquote')) return 'blockquote';
  if (editor.isActive('codeBlock')) return 'pre';
  for (let level = 1; level <= 6; level += 1) {
    if (editor.isActive('heading', { level })) return 'h' + level;
  }
  return 'p';
};

/**
 * font-family をツールバーの比較用に正規化する
 * 保存往復でブラウザが "Impact,sans-serif" を "Impact, sans-serif" にするため、そのままでは
 * FONT_LIST と一致せずツールバーがプレースホルダー表示に戻る
 */
export const normalizeFontFamily = (value: string | null | undefined): string => (value || '').replace(/,\s+/g, ',');

/** font-size (例 "18px") をツールバーの選択値へ。未設定は既定の 13 */
export const normalizeFontSize = (size: string | null | undefined): string =>
  size ? String(parseInt(size, 10)) : '13';

/**
 * リンク URL 欄の Enter で確定してよいか (IME 変換確定の Enter で閉じないようにする)
 * isComposing は確定直後の keydown で既に false に戻っているブラウザがあるので、
 * keyCode 229 (IME 処理中) も併用する
 */
export const shouldApplyLinkOnEnter = (event: EnterKeyLike, isComposing: boolean): boolean =>
  event.key === 'Enter' && !isComposing && event.keyCode !== 229;

/** インデント段数を増減する。0 未満・上限超えは丸める */
export const clampIndent = (current: number, delta: number): number =>
  Math.min(INDENT_MAX, Math.max(0, current + delta));

/** インデント段数 → margin-left の px 値 */
export const indentToMarginLeft = (indent: number): number => indent * INDENT_STEP_PX;

/** margin-left の指定 (例 "80px") → インデント段数。数値でなければ 0 */
export const marginLeftToIndent = (marginLeft: string): number => {
  const px = parseInt(marginLeft, 10);
  return Number.isFinite(px) ? Math.round(px / INDENT_STEP_PX) : 0;
};

/** テーブル挿入グリッドのラベル。「列 × 行」の順で出す (summernote に合わせる) */
export const tableSizeLabel = (rowIndex: number, colIndex: number): string => `${colIndex + 1} × ${rowIndex + 1}`;
